import React, {useMemo, useEffect, useState, useCallback, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
  Headline,
  Label,
  Body,
  designSystemToken,
} from '@lightricks/react-design-system';
import {Paper, Box, styled} from '@mui/material';
import {
  CollaborationGigCard,
  IncreaseEngagementBanner,
} from '@/components/gig-cards';
import EmptyState from '@/components/empty-state';
import FiltersButton from '@/components/filters-button';
import useGigsFilters from '@/hooks/use-gigs-filters';
import InfiniteGrid from '@/components/infinite-grid';
import StickyHeader from '@/components/sticky-header';
import translate from '@/utils/translate';
import {useUserData} from '@/hooks/useUserData';
import {SocialPlatform} from '@/types/platforms';
import {Profile} from '@/schema/User';
import {AvailableGig} from '@/types/gigs';
import Conditional from '@/components/conditional';
import Link from '@/components/link';
import useNavigation from '@/hooks/use-navigation';
import {media} from '@/styles/mediaQueries';
import GigsStore from '@/stores/gigsStore';
import {ConnectSocialAccountPopup} from '@/components/connect-social-account';
import {
  ERROR_MESSAGE_PARAM_KEY,
  ERROR_PROVIDER_PARAM_KEY,
} from '@/config/constants';
import useGenericButtonPressedEvent from '@/hooks/analytics/use-generic-button-pressed-event';
import useTrackScreenEvent from '@/hooks/analytics/use-track-screen-event';
import {screenNames, buttonNames} from '@/lib/delta/deltaConstants';
import DeltaHelper from '@/services/delta/DeltaHelper';

const FILL_PROFILE_BANNER_THRESHOLD = 70;

const StyledErrorState = styled(Paper)(() => ({
  padding: '40px',
  textAlign: 'center',
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  borderRadius: '8px',
  margin: '20px 0',
  color: designSystemToken('semantic.fg.danger'),
}));

const StyledExternalContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [media.mobile]: {
    'display': 'grid',
    'gridTemplateColumns': '1fr auto 1fr',
    'alignItems': 'center',
    '& > :first-of-type': {
      gridColumn: 2,
      justifySelf: 'center',
    },
    '& > :last-child': {
      gridColumn: 3,
      justifySelf: 'end',
    },
  },
}));

const StyledHeadline = styled(Headline)(() => ({
  [media.mobile]: {
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '1.43',
  },
}));

const StyledResultsCountLabel = styled(Label)(() => ({
  color: designSystemToken('semantic.fg.secondary'),
  marginTop: 12,
  marginBottom: 16,
  [media.mobile]: {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '1.43',
  },
}));

interface EngagementBannerContent {
  title: string;
  subtitle: string;
  buttonText: string;
  onButtonClick: () => void;
}

type EngagementBannerData = {
  randomizedPosition: number;
  bannerContent: EngagementBannerContent | null;
};

function getEngagementBannerContent(
  profile?: Profile,
  connectedPlatforms?: SocialPlatform[],
  navigation?: ReturnType<typeof useNavigation>,
  onConnectSocial?: () => void
): EngagementBannerContent | null {
  if (connectedPlatforms?.length === 0) {
    return {
      title: translate('Get Social To Get More Gigs!'),
      subtitle: translate(
        'Connect your social media accounts so more brands will want to collab'
      ),
      buttonText: translate('Connect social account'),
      onButtonClick: () => {
        onConnectSocial?.();
      },
    };
  }

  if (
    profile &&
    profile.profileProgressReport &&
    profile.profileProgressReport.total > 0
  ) {
    const profileProgressPercentage =
      (profile.profileProgressReport.points /
        profile.profileProgressReport.total) *
      100;
    if (profileProgressPercentage < FILL_PROFILE_BANNER_THRESHOLD) {
      return {
        title: translate('Fill Your Profile To Get More Gigs!'),
        subtitle: translate(
          'Complete your profile so more brands will want to collab'
        ),
        buttonText: translate('Edit profile'),
        onButtonClick: () => {
          navigation?.navigate('/account/profile');
        },
      };
    }
  }

  return null;
}

function CollaborationGigsItems({
  gigs,
  engagementBannerData,
}: {
  gigs: AvailableGig[];
  engagementBannerData: EngagementBannerData;
}) {
  const {pressed} = useGenericButtonPressedEvent();
  const increaseEngagementBanner = useMemo(() => {
    if (engagementBannerData.bannerContent) {
      return (
        <IncreaseEngagementBanner {...engagementBannerData.bannerContent} />
      );
    }
    return null;
  }, [engagementBannerData.bannerContent]);

  const trackButtonPressed = (buttonName: string) => {
    pressed({
      button_name: buttonName,
      screen_name: screenNames.suggested_gigs.suggested_gigs,
    });
  };

  // Create the items array with banner inserted at calculated position
  const items: JSX.Element[] = useMemo(() => {
    const gigElements = gigs.map((gig, index) => (
      <Link
        key={gig.id}
        to={`/campaigns/${gig.id}`}
        onClick={() => trackButtonPressed(`Suggested Gig: ${gig.campaignName}`)}
      >
        <CollaborationGigCard gigData={gig} />
      </Link>
    ));

    // Insert banner at the calculated position if it exists
    if (increaseEngagementBanner) {
      const result = [...gigElements];
      result.splice(
        Math.min(engagementBannerData.randomizedPosition, gigs.length),
        0,
        increaseEngagementBanner
      );
      return result;
    }

    return gigElements;
  }, [gigs, increaseEngagementBanner, engagementBannerData.randomizedPosition]);

  return (
    <>
      {items.map((item, index) => (
        <React.Fragment key={item.key || index}>{item}</React.Fragment>
      ))}
    </>
  );
}

function GigsCollectionView() {
  const {
    openFiltersModal: openFilters,
    filters,
    filterResultsCount,
    gigs: gigsData = [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGigsFilters();
  const {pressed} = useGenericButtonPressedEvent();
  const {presented: screenPresented, dismissed: screenDismissed} =
    useTrackScreenEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpenFilters = () => {
    openFilters();
    pressed({
      button_name: buttonNames.suggested_gigs.open_filters,
      screen_name: screenNames.suggested_gigs.suggested_gigs,
    });
  };

  const navigation = useNavigation();

  const {user, connectedPlatforms, isPlatformsInitialized} = useUserData({});

  // Connect Social Account Modal state
  const [isSocialAccountModalOpen, setIsSocialAccountModalOpen] =
    useState(false);
  const [wasModalClosed, setWasModalClosed] = useState(false);
  const gigsScreenPresentationIdRef = useRef<string>('');
  const openModal = useCallback(() => {
    setIsSocialAccountModalOpen(true);
    // Capture the current gigs screen presentation ID before switching to popup
    gigsScreenPresentationIdRef.current =
      DeltaHelper.getActiveScreenPresented().screen_presentation_id || '';
    screenPresented({
      screen_name: screenNames.connect_social_accounts.pop_up,
      source: screenNames.suggested_gigs.suggested_gigs,
    });
  }, [screenPresented]);
  const closeModal = useCallback(() => {
    setIsSocialAccountModalOpen(false);
    setWasModalClosed(true);
    screenDismissed({
      screen_name: screenNames.connect_social_accounts.pop_up,
      destination: screenNames.suggested_gigs.suggested_gigs,
    });
    // Restore the gigs screen as the active screen context, reusing the
    // original presentation ID so the page-level dismiss pairs correctly
    screenPresented({
      screen_name: screenNames.suggested_gigs.suggested_gigs,
      source: screenNames.connect_social_accounts.pop_up,
      screen_presentation_id: gigsScreenPresentationIdRef.current,
    });

    // Clear error parameters from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(ERROR_MESSAGE_PARAM_KEY);
    newSearchParams.delete(ERROR_PROVIDER_PARAM_KEY);
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams, screenDismissed, screenPresented]);

  // Check for error parameters on mount and open modal if they exist
  useEffect(() => {
    const message = searchParams.get(ERROR_MESSAGE_PARAM_KEY);
    const provider = searchParams.get(ERROR_PROVIDER_PARAM_KEY);

    if (
      message &&
      provider &&
      !isSocialAccountModalOpen &&
      !wasModalClosed &&
      isPlatformsInitialized &&
      connectedPlatforms.length === 0
    ) {
      openModal();
    }
  }, [
    searchParams,
    isSocialAccountModalOpen,
    wasModalClosed,
    openModal,
    isPlatformsInitialized,
    connectedPlatforms,
  ]);

  const engagementBannerRandomPosition =
    GigsStore.useEngagementBannerRandomPosition();

  const engagementBannerContent = useMemo(() => {
    return getEngagementBannerContent(
      user?.profile,
      connectedPlatforms,
      navigation,
      openModal
    );
  }, [user?.profile, connectedPlatforms, navigation, openModal]);

  // Handle loading more gigs when scrolling
  const handleLoadMore = async () => {
    if (!isFetchingNextPage && hasNextPage) {
      await fetchNextPage();
    }
  };

  if (error) {
    console.error(error);
    return (
      <StyledErrorState elevation={1}>
        <Body size="md">{translate('Error loading gigs')}</Body>
      </StyledErrorState>
    );
  }

  const resultGigsCount = filterResultsCount || 0;

  const shouldShowEmptyState =
    resultGigsCount === 0 && !engagementBannerContent && !isLoading;

  return (
    <>
      <StickyHeader>
        <StyledExternalContainer>
          <Box>
            <StyledHeadline size="xl">
              {translate('Suggested Gigs')}
            </StyledHeadline>
            <StyledResultsCountLabel size="lg">
              {isLoading
                ? translate('Loading Gigs...')
                : translate('X Gigs available_interval', {
                    postProcess: 'interval',
                    count: resultGigsCount,
                  })}
            </StyledResultsCountLabel>
          </Box>
          <FiltersButton
            onClick={handleOpenFilters}
            isDirty={filters?.isDirty}
          />
        </StyledExternalContainer>
      </StickyHeader>

      <Conditional
        condition={!shouldShowEmptyState}
        fallback={
          <EmptyState
            title={translate('No gigs available')}
            message={translate(
              'There are currently no gigs matching your filters.'
            )}
          />
        }
      >
        <InfiniteGrid
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          onLoadMore={handleLoadMore}
          isLoadingNextPage={isFetchingNextPage}
        >
          <CollaborationGigsItems
            gigs={gigsData}
            engagementBannerData={{
              randomizedPosition: engagementBannerRandomPosition,
              bannerContent: engagementBannerContent,
            }}
          />
        </InfiniteGrid>
      </Conditional>

      <ConnectSocialAccountPopup
        isOpen={isSocialAccountModalOpen}
        onClose={closeModal}
      />
    </>
  );
}

export default GigsCollectionView;
